import { Injectable, inject } from '@angular/core';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, UploadTaskSnapshot, listAll, getMetadata, ListResult, FullMetadata, StorageReference } from 'firebase/storage';
import { STORAGE_TOKEN } from '../firebase/storage.provider';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly storage = inject(STORAGE_TOKEN);

  /**
   * ファイルをFirebase Storageにアップロードし、進捗状況をObservableで返します。
   * @param path アップロード先のStorageパス
   * @param file アップロードするファイル
   * @returns 進捗状況のSnapshotを流すObservableとuploadTaskのオブジェクト
   */
  public uploadFile(path: string, file: File): { progress$: Observable<UploadTaskSnapshot>, uploadTask: ReturnType<typeof uploadBytesResumable> } {
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    const progress$ = new Observable<UploadTaskSnapshot>((subscriber) => {
      const unsubscribe = uploadTask.on(
        'state_changed',
        (snapshot) => subscriber.next(snapshot),
        (error) => subscriber.error(error),
        () => subscriber.complete()
      );

      return () => {
        unsubscribe();
        if (uploadTask.snapshot.state === 'running' || uploadTask.snapshot.state === 'paused') {
          uploadTask.cancel();
        }
      };
    });

    return { progress$, uploadTask };
  }

  /**
   * 指定したパスのダウンロードURLを取得します。
   * @param path Storageパス
   * @returns ダウンロードURLのPromise
   */
  public async getDownloadUrl(path: string): Promise<string> {
    const storageRef = ref(this.storage, path);
    return await getDownloadURL(storageRef);
  }

  /**
   * 指定したURLまたはパスのファイルを削除します。
   * @param urlOrPath 削除するファイルのStorage URLまたはパス
   */
  public async deleteFile(urlOrPath: string): Promise<void> {
    const storageRef = ref(this.storage, urlOrPath);
    await deleteObject(storageRef);
  }

  /**
   * 指定したパス配下のファイルおよびディレクトリ一覧を取得します。
   * @param path Storageパス
   * @returns ファイル・ディレクトリのリスト
   */
  public async listItems(path: string): Promise<ListResult> {
    const storageRef = ref(this.storage, path);
    return await listAll(storageRef);
  }

  /**
   * 指定したリファレンスのメタデータ（サイズ、作成日時など）を取得します。
   * @param reference Storageリファレンス
   * @returns メタデータ
   */
  public async getFileMetadata(reference: StorageReference): Promise<FullMetadata> {
    return await getMetadata(reference);
  }
}
